/**
 * The pool, laid out to pick from.
 *
 * Every slot in the organisation's current style, drawn through the one
 * resolver so a picked face looks exactly like the face that lands in the room.
 *
 * Slots an agent already wears are dimmed and cannot be picked. That is
 * advisory rather than enforced: a human is not in the pool's bookkeeping, so
 * an agent that arrives tomorrow can still be assigned the slot you are
 * wearing. Refusing the obvious clash you can see is worth doing anyway — it
 * costs nothing and it is the one a person would otherwise walk into.
 */
import { faceArt, FACE_SLOT_COUNT, type FaceSetView } from '../components/primitives'
import { radius } from '../components/primitives/shape'

/** Slot 7 is `face:07`, matching `services.face_slot_key` on the server. */
export function faceSlotKey(slot: number): string {
  return `face:${String(slot).padStart(2, '0')}`
}

const SLOTS = Array.from({ length: FACE_SLOT_COUNT }, (_, index) => index + 1)

export function FacePicker({ style, set, value, wornBy, onPick }: {
  /** The organisation's raw `face_style`: a built-in, or `set:<slug>`. */
  style: string
  /** `style` expanded, when it names the organisation's own pool. */
  set: FaceSetView | null
  /** The key currently worn, so the chosen one reads as chosen. */
  value: string | null
  /** Slot key to the agent wearing it. Anything in here cannot be picked. */
  wornBy: Record<string, string>
  onPick: (key: string) => void
}) {
  return (
    <div
      role="group"
      aria-label="Faces"
      style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(46px,1fr))',
        gap: 8, padding: 12, marginBottom: 18, borderRadius: radius.md,
        background: 'var(--panel)', border: '1px solid var(--border)',
      }}
    >
      {SLOTS.map((slot) => {
        const key = faceSlotKey(slot)
        const taken = wornBy[key]
        const on = value === key
        const art = faceArt('you', key, style, {}, set)
        // Every slot of every style is somebody, so the number is never shown.
        const who = art.figure ?? `Face ${slot}`
        return (
          <button
            key={key}
            aria-label={taken ? `${who}, worn by ${taken}` : who}
            aria-pressed={on}
            disabled={Boolean(taken)}
            title={taken ? `${who} — ${taken} wears this one` : who}
            onClick={() => onPick(key)}
            style={{
              aspectRatio: '1', padding: 3, borderRadius: radius.sm,
              cursor: taken ? 'not-allowed' : 'pointer',
              opacity: taken ? 0.25 : 1,
              background: on ? 'var(--accent-wash)' : 'transparent',
              border: `1px solid ${on ? 'var(--accent-edge)' : 'transparent'}`,
            }}
          >
            {art.kind === 'image'
              ? (
                <img
                  src={art.src}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-face)' }}
                />
              )
              : <span style={{ display: 'block', width: '100%', height: '100%', ...art.appearance }} />}
          </button>
        )
      })}
    </div>
  )
}
